import React, { memo, useContext, useEffect, useMemo, useState } from 'react';
import { makeStyles } from '@material-ui/core';
import { useDispatch } from 'react-redux';
import { getEntityCollection } from 'src/store/actions/entityInfoActions';
import { hexToRgba } from 'src/utils/colors';
// eslint-disable-next-line import/no-cycle
import EntityCard from 'src/components/EntityInspector/EntityInspectorDialog/EntityCard/EntityCard';
import EntityContext from 'src/components/EntityInspector/context';
const useStyles = makeStyles((theme) => ({
    wrapper: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
        '& > .entity-table:nth-child(odd)': {
            backgroundColor: hexToRgba(theme.palette.primary.main, 0.05)
        }
    },
    table: {
        color: theme.palette.text.color,
        borderLeft: `1px solid ${hexToRgba(theme.palette.primary.main, 0.5)}`
    },
    infoRow: {
        backgroundColor: theme.palette.primary.main,
        paddingLeft: theme.spacing(0.5),
        color: theme.palette.primary.contrastText
    },
    loadMoreBtn: {
        all: 'unset',
        cursor: 'pointer',
        color: theme.palette.primary.main
    }
}));
const PAGE_SIZE = 20;
function EntityCollection({ entity }) {
    const dispatch = useDispatch();
    const classes = useStyles();
    const { endpoint } = useContext(EntityContext);
    const [loading, setLoading] = useState(false);
    const [collection, setCollection] = useState();
    const [pagination, setPagination] = useState();
    const loadCollection = async (ent, page = 0) => {
        const { property, propertyValue, name } = ent;
        const { Pagination, Data } = await dispatch(getEntityCollection({
            Entity: name,
            Property: property,
            Value: propertyValue,
            PageSize: PAGE_SIZE,
            Page: page
        }, endpoint));
        setPagination(Pagination);
        setCollection((prev = []) => [...prev, ...Data]);
    };
    const hasMore = useMemo(() => {
        const total = pagination?.Total;
        if (!total)
            return false;
        const page = pagination.Page + 1;
        const loaded = page * pagination.PageSize;
        return loaded < total;
    }, [pagination]);
    const loadMore = () => {
        if (hasMore) {
            const nextPage = pagination.Page + 1;
            setLoading(true);
            loadCollection(entity, nextPage)
                .catch((e) => console.error(e))
                .finally(() => setLoading(false));
        }
    };
    useEffect(() => {
        setLoading(true);
        loadCollection(entity)
            .catch((e) => console.error(e))
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entity]);
    const primaryKeyIdx = useMemo(() => {
        if (!collection?.length)
            return null;
        return collection[0].findIndex((attr) => attr.IsPrimaryKey);
    }, [collection]);
    const primaryKeyValue = useMemo(() => {
        if (primaryKeyIdx === null)
            return null;
        return collection?.[0]?.[primaryKeyIdx]?.Value;
    }, [collection, primaryKeyIdx]);
    const infoText = useMemo(() => {
        if (loading)
            return 'Loading...';
        const total = pagination?.Total;
        const displayed = collection?.length;
        const currentPage = pagination?.Page;
        return `Collection size: ${total}, Displayed: ${displayed}, Page idx: ${currentPage}`;
    }, [collection, loading, pagination]);
    const renderLoadMoreBtn = () => {
        if (!hasMore)
            return null;
        return (React.createElement("button", { type: "button", onClick: loadMore, className: classes.loadMoreBtn, disabled: loading }, loading ? 'Loading...' : 'Load more'));
    };
    if (loading && !collection)
        return React.createElement("div", null,
            React.createElement("i", null, "Loading..."));
    if (!collection?.length)
        return React.createElement("div", null,
            React.createElement("i", null, "No data"));
    return (React.createElement("div", { className: classes.wrapper },
        React.createElement("div", { className: classes.infoRow }, infoText),
        collection.map((collectionEntity) => (React.createElement(EntityCard, { key: collectionEntity[primaryKeyIdx].Value, primaryKeyValue: primaryKeyValue, attributes: collectionEntity }))),
        renderLoadMoreBtn()));
}
export default memo(EntityCollection);
